import {CaretRightOutlined} from '@ant-design/icons';
import {Button, Checkbox, Collapse, InputNumber, Radio} from 'antd';
import PropTypes from 'prop-types';
import {useEffect, useState} from 'react';

import {RqGetAllProducts} from '../../../../reactQuery/apis';

const FilterOptions = ({filters, setFilters, resetFilters}) => {
  const {Panel} = Collapse;

  const {data: products = []} = RqGetAllProducts();

  const [activeKey, setActiveKey] = useState([]);

  // Local state for price fields
  const [priceFilters, setPriceFilters] = useState({
    minPrice: '',
    maxPrice: '',
  });

  useEffect(() => {
    setPriceFilters({
      minPrice: filters.minPrice || '',
      maxPrice: filters.maxPrice || '',
    });
  }, [filters.minPrice, filters.maxPrice]);

  const capitalizeEachWord = str =>
    str
      .split(' ')
      .map(
        word =>
          word.charAt(0).toUpperCase() + word.slice(1).toLowerCase(),
      )
      .join(' ');

  const jewelryType = [
    ...new Set(products.map(product => product?.jewelryType)),
  ]
    .filter(Boolean)
    .map(type => ({
      value: type,
      label: capitalizeEachWord(type),
    }));

  const productType = [
    ...new Set(products.map(product => product?.productType?.toLowerCase())),
  ]
    .filter(Boolean)
    .map(type => ({
      value: type,
      label: capitalizeEachWord(type),
    }));

  const handleFilterChange = (filterType, values) => {
    setFilters(prev => ({
      ...prev,
      [filterType]: values,
    }));
  };

  const handlePriceChange = (type, value) => {
    setPriceFilters(prev => ({
      ...prev,
      [type]: value,
    }));

    setFilters(prev => ({
      ...prev,
      [type]: value ? Number(value) : '',
    }));
  };

  const clearSingleSection = type => {
    switch (type) {
      case 'jewelryType':
        setFilters(prev => ({...prev, jewelryType: []}));
        break;
      case 'productType':
        setFilters(prev => ({...prev, productType: []}));
        break;
      case 'price':
        setPriceFilters({minPrice: '', maxPrice: ''});
        setFilters(prev => ({
          ...prev,
          minPrice: '',
          maxPrice: '',
        }));
        break;
      default:
        resetFilters();
    }
  };

  return (
    <div className={'w-full flex flex-col gap-3 p-2'}>
      {/* Clear All */}
      <div className={'ml-auto'}>
        <Button
          variant={'link'}
          className={'bg-white border-0 shadow-none !text-primary-900 p-0 font-semibold'}
          onClick={resetFilters}
        >
          Clear All Filters
        </Button>
      </div>

      <Collapse
        className={'w-full bg-white border-none'}
        expandIcon={({isActive}) => (
          <CaretRightOutlined rotate={isActive ? 90 : 0} />
        )}
        expandIconPosition={'end'}
        onChange={setActiveKey}
        defaultActiveKey={['1']}
      >
        {/* Jewelry Type */}
        <Panel
          header={(
            <div
              className={`font-medium lg:text-base ${
                activeKey.includes('1') ? 'text-primary-900' : ''
              }`}
            >
              Jewellery Type
            </div>
          )}
          key={'1'}
        >
          <Button
            variant={'link'}
            className={'p-0 border-0 !text-primary-950 font-semibold'}
            onClick={() => clearSingleSection('jewelryType')}
          >
            Reset
          </Button>

          <Checkbox.Group
            options={jewelryType}
            value={filters.jewelryType || []}
            onChange={values => handleFilterChange('jewelryType', values)}
            className={'flex flex-col gap-2 mt-2'}
          />
        </Panel>

        {/* Product Types */}
        <Panel
          header={(
            <div
              className={`font-medium lg:text-base ${
                activeKey.includes('2') ? 'text-primary-900' : ''
              }`}
            >
              Product Types
            </div>
          )}
          key={'2'}
        >
          <Button
            variant={'link'}
            className={'p-0 border-0 !text-primary-950 font-semibold'}
            onClick={() => clearSingleSection('productType')}
          >
            Reset
          </Button>

          <Checkbox.Group
            options={productType}
            value={filters.productType || []}
            onChange={values => handleFilterChange('productType', values)}
            className={'flex flex-col gap-2 mt-2'}
          />
        </Panel>

        {/* Price */}
        <Panel
          header={(
            <div
              className={`font-medium lg:text-base ${
                activeKey.includes('3') ? 'text-primary-900' : ''
              }`}
            >
              Price
            </div>
          )}
          key={'3'}
        >
          <Button
            variant={'link'}
            className={'p-0 border-0 !text-primary-950 font-semibold'}
            onClick={() => clearSingleSection('price')}
          >
            Reset
          </Button>

          <div className={'flex gap-2 items-center mt-5'}>
            <InputNumber
              type={'number'}
              placeholder={'Min Price'}
              min={500}
              value={priceFilters.minPrice}
              onChange={value => handlePriceChange('minPrice', value)}
              className={'border h-8 rounded w-full'}
            />
            <div>To</div>
            <InputNumber
              type={'number'}
              placeholder={'Max Price'}
              min={1500}
              value={priceFilters.maxPrice}
              onChange={value => handlePriceChange('maxPrice', value)}
              className={'border h-8 rounded w-full'}
            />
          </div>
        </Panel>
      </Collapse>
    </div>
  );
};

FilterOptions.propTypes = {
  filters: PropTypes.object.isRequired,
  setFilters: PropTypes.func.isRequired,
  resetFilters: PropTypes.func.isRequired,
};

export default FilterOptions;
